import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthProvider';
import {
  Badge,
  Button,
  Card,
  Icon,
  Modal,
  PageHeader,
  Pagination,
  Select,
  Table,
  Tabs,
  type Tone,
} from '../../design';
import { useToast } from '../../design/Toast';
import { fmtDateTime, fmtRelative } from '../../lib/format';
import { queryParams, useUrlState } from '../../lib/urlState';
import {
  type AccessGrant,
  GRANT_PURPOSE,
  GRANT_STATUS,
  type GrantStatus,
  GrantStatusBadge,
  useLockerOptions,
} from '../operations/common';
import s from '../operations/operations.module.css';
import { useOrgOptions } from '../users/UsersPage';
import { RemoteOpenDialog } from './RemoteOpenDialog';
import { RemoteOpenDrawer } from './RemoteOpenDrawer';
import { ExportButton } from '../reports/ExportButton';

export type RemoteOpenRequest = components['schemas']['RemoteOpenRequest'];
export type RemoteOpenStatus = components['schemas']['RemoteOpenStatus'];

export const REMOTE_STATUS: Record<RemoteOpenStatus, { label: string; tone: Tone }> = {
  pending: { label: 'De aprobat', tone: 'orange' },
  approved: { label: 'Aprobată', tone: 'blue' },
  executed: { label: 'Executată', tone: 'green' },
  failed: { label: 'Eșuată', tone: 'red' },
  rejected: { label: 'Respinsă', tone: 'neutral' },
  expired: { label: 'Expirată', tone: 'neutral' },
};

type Tab = 'chei' | 'deschidere';

export function KeysPage() {
  const { has } = useAuth();
  const [params, setParams] = useSearchParams();
  const canRemote = has('open') || has('approve');
  const tab: Tab = params.get('tab') === 'deschidere' && canRemote ? 'deschidere' : 'chei';
  const setTab = (t: Tab) =>
    setParams(
      () => {
        const next = new URLSearchParams();
        if (t !== 'chei') next.set('tab', t);
        return next;
      },
      { replace: true },
    );
  return (
    <>
      <PageHeader
        title="Chei de acces"
        subtitle="Autorizații limitate la o acțiune, un compartiment și un interval explicit. Fără coduri master; codurile nu sunt vizibile după emitere."
      />
      {canRemote && (
        <Tabs
          value={tab}
          onChange={setTab}
          items={[
            { value: 'chei', label: 'Chei de acces' },
            { value: 'deschidere', label: 'Deschidere administrativă' },
          ]}
        />
      )}
      {tab === 'chei' ? <GrantsTab /> : <RemoteOpenTab />}
    </>
  );
}

function GrantsTab() {
  const { user, has } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const { values, page, pageSize, set } = useUrlState(['status', 'lockerId', 'orgId'] as const);
  const orgs = useOrgOptions();
  const lockers = useLockerOptions();
  const lockerName = (id: string) => lockers.data?.items.find((l) => l.id === id)?.name ?? '…';
  const [revoking, setRevoking] = useState<AccessGrant | null>(null);
  const query = useQuery({
    queryKey: ['access-grants', { ...values, page, pageSize }],
    queryFn: () =>
      unwrap(
        api.GET('/access-grants', {
          params: {
            query: queryParams({
              status: (values.status || undefined) as GrantStatus | undefined,
              lockerId: values.lockerId || undefined,
              orgId: values.orgId || undefined,
              page,
              pageSize,
            }),
          },
        }),
      ),
  });
  const revoke = useMutation({
    mutationFn: (g: AccessGrant) =>
      unwrap(api.POST('/access-grants/{id}/revoke', { params: { path: { id: g.id } } })),
    onSuccess: () => {
      toast('Cheia a fost revocată.', 'success');
      setRevoking(null);
      void qc.invalidateQueries({ queryKey: ['access-grants'] });
      void qc.invalidateQueries({ queryKey: ['operation'] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  return (
    <Card tight>
      <div className="flex wrap" style={{ padding: 16 }}>
        <Select
          value={values.status}
          onChange={(e) => set({ status: e.target.value })}
          style={{ maxWidth: 200 }}
          aria-label="Stare"
        >
          <option value="">Toate stările</option>
          {(Object.keys(GRANT_STATUS) as GrantStatus[]).map((st) => (
            <option key={st} value={st}>
              {GRANT_STATUS[st].label}
            </option>
          ))}
        </Select>
        <Select
          value={values.lockerId}
          onChange={(e) => set({ lockerId: e.target.value })}
          style={{ maxWidth: 240 }}
          aria-label="Locker"
        >
          <option value="">Toate lockerele</option>
          {lockers.data?.items.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </Select>
        {user?.orgId === null && (
          <Select
            value={values.orgId}
            onChange={(e) => set({ orgId: e.target.value })}
            style={{ maxWidth: 240 }}
            aria-label="Organizație"
          >
            <option value="">Toate organizațiile</option>
            {orgs.data?.items.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        )}
        <ExportButton
          kind="access-grants"
          params={{
            status: values.status || undefined,
            lockerId: values.lockerId || undefined,
            orgId: values.orgId || undefined,
          }}
        />
      </div>
      <Table<AccessGrant>
        columns={[
          {
            key: 'key',
            header: 'Cheie',
            render: (g) => (
              <>
                <span className={s.mask}>{'•'.repeat(g.codeLength)}</span>
                <div className="small muted">
                  <Badge tone={g.purpose === 'deposit' ? 'purple' : 'blue'}>
                    {GRANT_PURPOSE[g.purpose]}
                  </Badge>
                </div>
              </>
            ),
          },
          {
            key: 'op',
            header: 'Operațiune',
            render: (g) => (
              <Link to={`/operatiuni/${g.operationId}`} onClick={(e) => e.stopPropagation()}>
                Deschide operațiunea
              </Link>
            ),
          },
          { key: 'locker', header: 'Locker', render: (g) => lockerName(g.lockerId) },
          {
            key: 'valid',
            header: 'Valabilă',
            render: (g) => (
              <>
                {fmtDateTime(g.validFrom)}
                <div className="small muted">până la {fmtDateTime(g.expiresAt)}</div>
              </>
            ),
          },
          { key: 'status', header: 'Stare', render: (g) => <GrantStatusBadge status={g.status} /> },
          {
            key: 'by',
            header: 'Emisă de',
            render: (g) => <span className="muted">{g.createdBy}</span>,
          },
          {
            key: 'actions',
            header: '',
            align: 'right',
            render: (g) =>
              has('keys') && g.status === 'active' ? (
                <Button small variant="danger" onClick={() => setRevoking(g)}>
                  Revocă
                </Button>
              ) : null,
          },
        ]}
        rows={query.data?.items ?? []}
        rowKey={(g) => g.id}
        empty={
          query.isLoading
            ? 'Se încarcă…'
            : query.error
              ? errorMessage(query.error)
              : 'Nicio cheie pentru filtrele alese.'
        }
        footer={
          query.data && (
            <Pagination
              page={query.data.page}
              pageSize={query.data.pageSize}
              total={query.data.total}
              onPage={(p) => set({ page: p })}
            />
          )
        }
      />
      {revoking && (
        <Modal
          title="Revocă cheia"
          onClose={() => setRevoking(null)}
          footer={
            <>
              <Button onClick={() => setRevoking(null)}>Renunță</Button>
              <Button
                variant="danger"
                onClick={() => revoke.mutate(revoking)}
                busy={revoke.isPending}
              >
                Confirmă revocarea
              </Button>
            </>
          }
        >
          <p>
            Cheia de {GRANT_PURPOSE[revoking.purpose].toLowerCase()} nu va mai fi acceptată la
            kiosk. Operațiunea rămâne; poți emite o cheie nouă din detaliul ei.
          </p>
        </Modal>
      )}
    </Card>
  );
}

function RemoteOpenTab() {
  const { has } = useAuth();
  const { values, page, pageSize, set } = useUrlState(['status', 'lockerId'] as const);
  const lockers = useLockerOptions();
  const lockerName = (id: string) => lockers.data?.items.find((l) => l.id === id)?.name ?? '…';
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['remote-open', { ...values, page, pageSize }],
    queryFn: () =>
      unwrap(
        api.GET('/remote-open-requests', {
          params: {
            query: queryParams({
              status: (values.status || undefined) as RemoteOpenStatus | undefined,
              lockerId: values.lockerId || undefined,
              page,
              pageSize,
            }),
          },
        }),
      ),
    refetchInterval: 15_000,
  });
  return (
    <>
      <div className={s.warn} style={{ margin: '12px 0 16px' }}>
        <Icon name="shield" size={18} />
        <span>
          Acces fizic cu circuit separat: motiv obligatoriu, verificare 2FA la cerere și la
          aprobare, aprobator diferit de solicitant. Cererile expiră în 15 minute. Deschiderea nu
          modifică ocuparea compartimentului.
        </span>
      </div>
      <Card tight>
        <div className="flex between wrap" style={{ padding: 16 }}>
          <div className="flex wrap">
            <Select
              value={values.status}
              onChange={(e) => set({ status: e.target.value })}
              style={{ maxWidth: 200 }}
              aria-label="Stare cerere"
            >
              <option value="">Toate stările</option>
              {(Object.keys(REMOTE_STATUS) as RemoteOpenStatus[]).map((st) => (
                <option key={st} value={st}>
                  {REMOTE_STATUS[st].label}
                </option>
              ))}
            </Select>
            <Select
              value={values.lockerId}
              onChange={(e) => set({ lockerId: e.target.value })}
              style={{ maxWidth: 240 }}
              aria-label="Locker cerere"
            >
              <option value="">Toate lockerele</option>
              {lockers.data?.items.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </Select>
          </div>
          {has('open') && (
            <Button variant="primary" icon="shield" onClick={() => setCreating(true)}>
              Cerere nouă
            </Button>
          )}
        </div>
        <Table<RemoteOpenRequest>
          columns={[
            {
              key: 'reason',
              header: 'Cerere',
              render: (r) => (
                <>
                  <span
                    style={{
                      display: 'block',
                      maxWidth: 320,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {r.reason}
                  </span>
                  {r.ticket && <div className="small muted mono">{r.ticket}</div>}
                </>
              ),
            },
            {
              key: 'locker',
              header: 'Locker / compartiment',
              render: (r) => (
                <>
                  {lockerName(r.lockerId)}
                  <div className="small muted">Compartimentul {r.slotLabel}</div>
                </>
              ),
            },
            { key: 'requester', header: 'Solicitant', render: (r) => r.requesterLabel },
            {
              key: 'when',
              header: 'Cerută',
              render: (r) => (
                <>
                  {fmtRelative(r.requestedAt)}
                  <div className="small muted">
                    {r.status === 'pending'
                      ? `expiră ${fmtRelative(r.expiresAt)}`
                      : fmtDateTime(r.requestedAt)}
                  </div>
                </>
              ),
            },
            {
              key: 'status',
              header: 'Stare',
              render: (r) => (
                <Badge tone={REMOTE_STATUS[r.status].tone}>{REMOTE_STATUS[r.status].label}</Badge>
              ),
            },
            {
              key: 'approver',
              header: 'Aprobator / rezultat',
              render: (r) => (
                <>
                  {r.approverLabel ?? <span className="muted">—</span>}
                  {r.failureReason && (
                    <div className="small" style={{ color: 'var(--red)' }}>
                      {r.failureReason}
                    </div>
                  )}
                </>
              ),
            },
          ]}
          rows={query.data?.items ?? []}
          rowKey={(r) => r.id}
          onRowClick={(r) => setOpenId(r.id)}
          empty={
            query.isLoading
              ? 'Se încarcă…'
              : query.error
                ? errorMessage(query.error)
                : 'Nicio cerere.'
          }
          footer={
            query.data && (
              <Pagination
                page={query.data.page}
                pageSize={query.data.pageSize}
                total={query.data.total}
                onPage={(p) => set({ page: p })}
              />
            )
          }
        />
      </Card>
      {creating && (
        <RemoteOpenDialog
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            setOpenId(id);
          }}
        />
      )}
      {openId && <RemoteOpenDrawer id={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}
